import Box from "@mui/material/Box";
import LabelValueField from "components/Label/LabelValueField";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { TFieldType, TOption } from "type";
import { SxProps, Theme } from "@mui/material";
import { IInfoCell } from "features/mst_store_tire_wheel_filter/types/wheel";
import { CostSettingInfo } from "features/mst_proc_fee/types/proc-fee";
import {
  CASHBACK_PERIOD_OPTIONS,
  PUNCTURE_COMPENSATION_OPTIONS,
  PUNCTURE_COMPENSATION_WHEEL_OPTIONS,
  ROUNDING_OPTIONS,
} from "features/mst_proc_fee/constants/options";
import { SESSION_KEY } from "constants/key";
import routes from "constants/routes";
import Barcode from "components/Barcode";

// Constants
const ENABLED_TEXT = "する";
const DISABLED_TEXT = "なし";
const TAKE_OUT_DISABLED_TEXT = "しない";
const CSS_VARIABLES = {
  GREEN: "var(--green-600)",
  OFFWHITE: "var(--offwhite-500)",
} as const;

/** ナット / ロックナット: 表示値の高さを内容に合わせる */
const NUT_LOCK_VALUE_SX: SxProps<Theme> = {
  height: "auto",
  minHeight: { xs: "48px", xl: "48px" },
  textAlign: "center",
  alignItems: "center",
  "& .MuiOutlinedInput-root": {
    height: "auto",
  },
  "& .MuiSelect-select": {
    height: "auto",
    minHeight: "48px",
    whiteSpace: "normal",
    lineHeight: 1.3,
    alignItems: "center",
    py: 1,
  },
};

const STATIC_OPTIONS = {
  TAKE_OUT: [
    { label: "する", value: "0" },
    { label: "なし", value: "1" },
  ] as TOption[],
};

const DEFAULT_VALUES = {
  TAKE_OUT: "1",
  WHEEL_COATING: "2,000円",
} as const;

// Helper functions
const getBackgroundColor = (isActive: boolean) =>
  isActive ? CSS_VARIABLES.GREEN : CSS_VARIABLES.OFFWHITE;

const getValueAndType = (
  flag: boolean,
  value: string,
  readOnly: boolean,
): { displayValue: string; valueType: TFieldType } => ({
  displayValue: flag ? value : DISABLED_TEXT,
  valueType: !readOnly ? (flag ? "select" : "text") : "text",
});

// For 車両への取付 only: checkbox checked → する, unchecked → なし (no dropdown)
const getTakeOutValueAndType = (
  flag: boolean,
): { value: string; valueType: TFieldType } => ({
  value: flag ? ENABLED_TEXT : TAKE_OUT_DISABLED_TEXT,
  valueType: "text",
});

/**
 * Formats cost value for detail view based on flag status
 * @param flag - The flag value (null, 0, 1, boolean, or string)
 * @param value - The cost value as string or number
 * @returns Formatted display value: "-" if flag is null/0, "無料" if flag is 1 and value is 0, otherwise the value
 */
export const formatCostValueForDetail = (
  flag: boolean | number | string | null | undefined,
  value: string | number | undefined,
): string => {
  // If flag is null or undefined, display "-"
  if (flag === null || flag === undefined) {
    return "-";
  }

  // Convert flag to number for comparison
  let flagNum: number;
  if (typeof flag === "boolean") {
    flagNum = flag ? 1 : 0;
  } else if (typeof flag === "string") {
    flagNum = flag === "1" || flag === "true" ? 1 : 0;
  } else {
    flagNum = flag;
  }

  // If flag is 0, display "-"
  if (flagNum === 0) {
    return "-";
  }

  // If flag is 1, check the value
  if (flagNum === 1) {
    const numValue =
      typeof value === "string" ? Number(value.replace(/[^0-9]/g, "")) : Number(value ?? 0);
    if (numValue === 0) {
      return "無料";
    }
    return String(value ?? "");
  }

  return String(value ?? "");
};

interface ServiceCostSummaryProps {
  isBarCode?: boolean;
  type: "confirm" | "detail";
  sxBox?: SxProps<Theme>;
  costSettingInfo: CostSettingInfo;
  setCostSettingInfo?: (costSettingInfo: CostSettingInfo) => void;
  costItems?: IInfoCell[];
  quotationSummaryItems?: IInfoCell[];
  selectOptions?: {
    nat: TOption[];
    lockNat: TOption[];
    hub: TOption[];
    wcoating: TOption[];
  };
  disablePunctureAndCashback?: boolean;
  hideValve?: boolean;
  barcodeCodes?: {
    nut_jan_cd?: string;
    locknut_jan_cd?: string;
    hubring_jan_cd?: string;
    wcoating_jan_cd?: string;
  };
}

type FlagFieldMapping = {
  [key: string]: {
    valueField: keyof CostSettingInfo;
    getDefaultValue: (options: any) => string;
  };
};

/**
 * ServiceCostSummary Component
 *
 * Displays a summary of service costs for tire and wheel-related services.
 * Supports two modes: "confirm" (editable) and "detail" (read-only).
 * Renders cost items and quotation summary items, allowing editing or viewing based on the mode.
 * @param {ServiceCostSummaryProps} props - The props for the component.
 * @param {"confirm" | "detail"} [props.type] - Determines if the summary is editable ("confirm") or read-only ("detail").
 * @param {CostSettingInfo} props.costSettingInfo - Wheel and service cost information.
 * @param {Function} [props.setCostSettingInfo] - Function to update costSettingInfo state.
 * @param {IInfoCell[]} [props.costItems] - Custom cost items configuration.
 * @param {IInfoCell[]} [props.quotationSummaryItems] - Custom quotation summary items configuration.
 * @param {object} [props.barcodeCodes] - JAN codes for barcodes (nut, locknut, hubring, wcoating).
 */
function ServiceCostSummary({
  isBarCode = false,
  type = "confirm",
  sxBox,
  costSettingInfo,
  setCostSettingInfo,
  costItems: customCostItems,
  quotationSummaryItems: customQuotationSummaryItems,
  selectOptions,
  disablePunctureAndCashback = false,
  hideValve = false,
  barcodeCodes,
}: ServiceCostSummaryProps) {
  const readOnly = type === "detail";

  const costSettingRef = useRef(costSettingInfo);
  costSettingRef.current = costSettingInfo;

  // Detect if this is wheel flow (from WheelTireProcFeeList) - only 2 puncture options
  const isWheelFlow = useMemo(() => {
    try {
      const from = sessionStorage.getItem(SESSION_KEY.TIRE_WHEEL_TIRE_FROM);
      return from === routes.mstWheelTireProcFee;
    } catch {
      return false;
    }
  }, []);

  // Use appropriate puncture options based on flow
  const punctureOptions = isWheelFlow
    ? PUNCTURE_COMPENSATION_WHEEL_OPTIONS
    : PUNCTURE_COMPENSATION_OPTIONS;

  const options = useMemo(
    () =>
      selectOptions || {
        nat: [],
        lockNat: [],
        hub: [],
        wcoating: [],
      },
    [selectOptions],
  );

  // Flag field mapping for cleaner logic
  const flagFieldMapping: FlagFieldMapping = useMemo(
    () => ({
      nut_flag: {
        valueField: "nut",
        getDefaultValue: (opts) => String(opts.nat[0]?.value || ""),
      },
      lock_nut_flag: {
        valueField: "lock_nut",
        getDefaultValue: (opts) => String(opts.lockNat[0]?.value || ""),
      },
      hub_ring_flag: {
        valueField: "hub_ring",
        getDefaultValue: (opts) => String(opts.hub[0]?.value || ""),
      },
      take_out_flag: {
        valueField: "take_out",
        getDefaultValue: () => DEFAULT_VALUES.TAKE_OUT,
      },
      wheel_coating_flag: {
        valueField: "wheel_coating",
        getDefaultValue: (opts) => String(opts.wcoating[0]?.value || ""),
      },
    }),
    [options],
  );

  // Helper function to get label from options by value
  const getLabelFromOptions = useCallback(
    (optionsList: TOption[], value: string | number): string => {
      const option = optionsList.find((opt) => opt.value === value);
      return option?.label || String(value);
    },
    [],
  );

  // Helper function to create cost item configuration
  const createCostItem = useCallback(
    (
      label: string,
      fieldName: keyof CostSettingInfo,
      fieldNameLabel?: keyof CostSettingInfo,
      customConfig?: Partial<IInfoCell>,
    ): IInfoCell => {
      // Fields that need special formatting in detail view
      const fieldsRequiringFormat = [
        "waste_tire_cost",
        "rubber_stem_valve_cost",
        "nitrogen_inflation_cost",
        "hub_rust_prevention_cost",
      ];

      // Get the raw value
      const rawValue = costSettingInfo[fieldName] as string | number;

      // Format value for detail view if needed
      let displayValue = rawValue;
      if (readOnly && fieldNameLabel && fieldsRequiringFormat.includes(fieldName as string)) {
        const flagValue = costSettingInfo[fieldNameLabel];
        displayValue = formatCostValueForDetail(flagValue, rawValue);
      }

      const baseConfig: IInfoCell = {
        sxBox: { width: "142px" },
        label,
        labelType: fieldNameLabel && !readOnly ? "checkbox" : "text",
        sxLabel: {
          backgroundColor: fieldNameLabel
            ? getBackgroundColor(Boolean(costSettingInfo[fieldNameLabel]))
            : CSS_VARIABLES.GREEN,
          whiteSpace: "nowrap",
        },
        value: displayValue,
        valueType: !readOnly ? "input" : "text",
        sxValue: { textAlign: "center" },
        fieldName: fieldName as string,
        ...(fieldNameLabel && { fieldNameLabel: fieldNameLabel as string }),
      };

      return { ...baseConfig, ...customConfig };
    },
    [costSettingInfo, readOnly],
  );

  /**
   * Memoized list of cost item configurations for the summary.
   * Each item describes how to render a row in the summary.
   */
  const costItems: IInfoCell[] = useMemo(
    () =>
      customCostItems || [
        createCostItem("交換工賃", "replac_labor_cost"),
        createCostItem("処分", "waste_tire_cost", "waste_tire_cost_flag"),
        ...(!hideValve ? [createCostItem("バルブ", "rubber_stem_valve_cost", "rubber_stem_valve_flag")] : []),
        createCostItem("窒素", "nitrogen_inflation_cost", "nitrogen_inflation_flag"),
        createCostItem("ハブ防錆", "hub_rust_prevention_cost", "hub_rust_prevention_flag", {
          valueOnly: true,
        }),
        createCostItem("パンク補償", "tire_puncture_flag", undefined, {
          labelType: "text",
          sxLabel: { backgroundColor: CSS_VARIABLES.OFFWHITE, whiteSpace: "nowrap" },
          valueType: !readOnly && !disablePunctureAndCashback ? "select" : "text",
          value: disablePunctureAndCashback
            ? getLabelFromOptions(punctureOptions, costSettingInfo.tire_puncture_flag)
            : costSettingInfo.tire_puncture_flag,
          options: punctureOptions,
          valueOnly: true,
          disabled: disablePunctureAndCashback,
        }),
        createCostItem("キャッシュバック", "cashback_period", "cashback_flag", {
          sxBox: { width: "210px" },
          sxLabel: {
            p: "2px",
            backgroundColor: getBackgroundColor(Boolean(costSettingInfo.cashback_flag)),
            whiteSpace: "nowrap",
          },
          valueType: !readOnly && !disablePunctureAndCashback ? "select" : "text",
          value: disablePunctureAndCashback
            ? getLabelFromOptions(CASHBACK_PERIOD_OPTIONS, costSettingInfo.cashback_period)
            : costSettingInfo.cashback_period,
          options: CASHBACK_PERIOD_OPTIONS,
          valueOnly: true,
          disabled: disablePunctureAndCashback,
        }),
        createCostItem("丸め", "rounding", undefined, {
          labelType: "text",
          sxLabel: { backgroundColor: CSS_VARIABLES.OFFWHITE, whiteSpace: "nowrap" },
          valueType: !readOnly ? "select" : "text",
          options: ROUNDING_OPTIONS,
          valueOnly: true,
        }),
      ],
    [
      costSettingInfo,
      readOnly,
      customCostItems,
      createCostItem,
      disablePunctureAndCashback,
      hideValve,
      getLabelFromOptions,
      punctureOptions,
    ],
  );

  // Helper function to create quotation summary item configuration
  const createQuotationItem = useCallback(
    (
      label: string,
      fieldName: keyof CostSettingInfo,
      fieldNameLabel: keyof CostSettingInfo,
      optionsList: TOption[],
      customConfig?: Partial<IInfoCell>,
    ): IInfoCell => {
      const flag = Boolean(costSettingInfo[fieldNameLabel]);
      const { displayValue, valueType } = getValueAndType(
        flag,
        costSettingInfo[fieldName] as string,
        readOnly,
      );

      const baseConfig: IInfoCell = {
        sxBox: { width: 260 },
        label,
        labelType: !readOnly ? "checkbox" : "text",
        fieldNameLabel: fieldNameLabel as string,
        sxLabel: {
          backgroundColor: getBackgroundColor(flag),
          whiteSpace: "nowrap",
        },
        value: displayValue,
        valueType,
        sxValue: { textAlign: "center" },
        options: optionsList,
        fieldName: fieldName as string,
        valueOnly: true,
      };

      return { ...baseConfig, ...customConfig };
    },
    [costSettingInfo, readOnly],
  );

  const quotationSummaryItems: IInfoCell[] = useMemo(
    () =>
      customQuotationSummaryItems || [
        (() => {
          // take_out_flag 0 / true / "0" → する + GREEN; 1 / false / "1" → しない + OFFWHITE
          const rawTakeOut = costSettingInfo.take_out_flag as boolean | number | string | undefined;
          const isTakeOutSuru =
            rawTakeOut === 0 ||
            rawTakeOut === true ||
            rawTakeOut === "0";
          return createQuotationItem(
            "車両への取付",
            "take_out",
            "take_out_flag",
            STATIC_OPTIONS.TAKE_OUT,
            {
              ...getTakeOutValueAndType(isTakeOutSuru),
              sxLabel: {
                backgroundColor: isTakeOutSuru ? CSS_VARIABLES.GREEN : CSS_VARIABLES.OFFWHITE,
                whiteSpace: "nowrap",
              },
            },
          );
        })(),
        createQuotationItem("ナット", "nut", "nut_flag", options.nat, {
          sxValue: NUT_LOCK_VALUE_SX,
          selectValueMultiline: true,
        }),
        createQuotationItem("ロックナット", "lock_nut", "lock_nut_flag", options.lockNat, {
          sxValue: NUT_LOCK_VALUE_SX,
          selectValueMultiline: true,
        }),
        createQuotationItem("ハブリング", "hub_ring", "hub_ring_flag", options.hub, {
          sxValue: NUT_LOCK_VALUE_SX,
          selectValueMultiline: true,
        }),
        createQuotationItem(
          "ホイールコーティング",
          "wheel_coating",
          "wheel_coating_flag",
          options.wcoating, {
          sxValue: NUT_LOCK_VALUE_SX,
          selectValueMultiline: true,
        }
        ),
      ],
    [costSettingInfo, readOnly, customQuotationSummaryItems, options, createQuotationItem],
  );

  const onChangeValue = useCallback(
    (key: string, value: string | boolean, valueType?: TFieldType) => {
      if (readOnly || !setCostSettingInfo) return;

      // Prevent changes to パンク補償 and キャッシュバック when disabled
      if (
        disablePunctureAndCashback &&
        (key === "tire_puncture_flag" || key === "cashback_period" || key === "cashback_flag")
      ) {
        return;
      }

      let newValue = value;
      if (valueType === "input" && typeof value === "string") {
        newValue = value.replace(/[^0-9]/g, "");
      }

      const updatedCostSettingInfo = { ...costSettingInfo, [key]: newValue } as CostSettingInfo;

      if (key === "lock_nut" || key === "lock_nut_flag") {
        updatedCostSettingInfo.nut = DISABLED_TEXT;
        updatedCostSettingInfo.nut_flag = false;
      }

      // Handle flag-based default values using mapping
      const flagMapping = flagFieldMapping[key];
      if (flagMapping) {
        const targetField = flagMapping.valueField;
        const newFieldValue = value === true ? flagMapping.getDefaultValue(options) : DISABLED_TEXT;

        // Create new object with updated field to avoid type issues
        Object.assign(updatedCostSettingInfo, { [targetField]: newFieldValue });
      }

      setCostSettingInfo(updatedCostSettingInfo);
    },
    [
      readOnly,
      setCostSettingInfo,
      costSettingInfo,
      flagFieldMapping,
      options,
      disablePunctureAndCashback,
    ],
  );

  // Fill defaults when option lists change only — ref reads latest cost so ナット selection is not cleared on each keystroke/re-render
  useEffect(() => {
    if (!setCostSettingInfo) return;
    const prev = costSettingRef.current;
    const updatedInfo = { ...prev };
    let hasChanges = false;

    Object.entries(flagFieldMapping).forEach(([flagKey, mapping]) => {
      const flagValue = Boolean(prev[flagKey as keyof CostSettingInfo]);
      const currentValue = prev[mapping.valueField];

      if (flagValue && (!currentValue || currentValue === "")) {
        (updatedInfo as any)[mapping.valueField] = mapping.getDefaultValue(options);
        hasChanges = true;
      } else if (!flagValue && (!currentValue || currentValue === "")) {
        (updatedInfo as any)[mapping.valueField] = DISABLED_TEXT;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setCostSettingInfo(updatedInfo);
    }
  }, [options, setCostSettingInfo, flagFieldMapping]);

  return (
    <Box sx={{ ...sxBox }}>
      <Box display="flex" gap="2px" flexWrap={{ xs: "wrap", xl: "nowrap" }} justifyContent="end">
        {costItems.map((el) => (
          <LabelValueField
            key={el.fieldName}
            sxBox={el.sxBox}
            label={el.label}
            sxLabel={el.sxLabel}
            labelType={el.labelType}
            value={el.value}
            valueType={el.valueType}
            sxValue={el.sxValue}
            options={el.options}
            valueOnly={el.valueOnly}
            checked={Boolean(costSettingInfo[el.fieldNameLabel as never])}
            onChangeLabel={(e) => onChangeValue(el.fieldNameLabel as never, e.target.checked)}
            onChangeValue={(e) => onChangeValue(el.fieldName, e.target.value, el.valueType)}
            disabled={el.disabled || readOnly}
          />
        ))}
      </Box>
      <Box display="flex" gap="2px" justifyContent="end">
        {quotationSummaryItems.map((el) => (
          <LabelValueField
            key={el.fieldName}
            sxBox={el.sxBox}
            label={el.label}
            sxLabel={el.sxLabel}
            labelType={el.labelType}
            value={el.value}
            valueType={el.valueType}
            sxValue={el.sxValue}
            options={el.options}
            valueOnly={el.valueOnly}
            selectValueMultiline={el.selectValueMultiline}
            checked={Boolean(costSettingInfo[el.fieldNameLabel as never])}
            onChangeLabel={(e) => onChangeValue(el.fieldNameLabel as never, e.target.checked)}
            onChangeValue={(e) => onChangeValue(el.fieldName, e.target.value)}
            disabled={readOnly}
          />
        ))}
      </Box>
      {isBarCode && (
        <Box sx={{ display: "flex", textAlign: "center", justifyContent: "end", gap: "2px" }}>
          <Box sx={{ width: 260, display: "flex", justifyContent: "center" }}>
            {barcodeCodes?.nut_jan_cd && costSettingInfo.nut_flag && (
              <Barcode value={barcodeCodes.nut_jan_cd} width={250} height={40} />
            )}
          </Box>
          <Box sx={{ width: 260, display: "flex", justifyContent: "center" }}>
            {barcodeCodes?.locknut_jan_cd && costSettingInfo.lock_nut_flag && (
              <Barcode value={barcodeCodes.locknut_jan_cd} width={250} height={40} />
            )}
          </Box>
          <Box sx={{ width: 260, display: "flex", justifyContent: "center" }}>
            {barcodeCodes?.hubring_jan_cd && (
              <Barcode value={barcodeCodes.hubring_jan_cd} width={250} height={40} />
            )}
          </Box>
          <Box sx={{ width: 260, display: "flex", justifyContent: "center" }}>
            {barcodeCodes?.wcoating_jan_cd && (
              <Barcode value={barcodeCodes.wcoating_jan_cd} width={250} height={40} />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default ServiceCostSummary;
